import { useCallback } from "react";
import { useFilter } from "react-aria";
import {
  ComboBox as AriaComboBox,
  type ComboBoxProps as AriaComboBoxProps,
} from "react-aria-components";
import { comboboxStyle } from "./style.js";

export type ComboboxSelectionMode = "single" | "multiple";

export interface ComboboxProps<
  T extends object,
  M extends ComboboxSelectionMode = "single",
> extends Omit<AriaComboBoxProps<T, M>, "className" | "style" | "menuTrigger"> {
  className?: string;
}

/**
 * Accessible combobox built on react-aria-components, following the W3C
 * WAI-ARIA combobox pattern. Compose with {@link ComboboxInput}
 * (single-select) or {@link ComboboxChips} (multi-select) for the field, and
 * {@link ComboboxContent} for the popup.
 *
 * The popup expands when clicking the field or its chevron, pressing
 * Down Arrow, or typing (which filters the options); tabbing into the field
 * does not expand it.
 *
 * Typing filters case-, accent- and whitespace-insensitively ("johnsmith"
 * finds "John Smith"); pass `defaultFilter` to override.
 *
 * Controlled usage — control the axes that represent your data, delegate the
 * widget mechanics:
 *
 * - `value`/`onChange`: always control.
 * - `inputValue`/`onInputChange`: control only when the flow needs the text
 *   (e.g. an "Add ..." custom-value option). When both `value` and
 *   `inputValue` are controlled, react-aria stops resetting/clearing the
 *   input on selection — handlers must update both (see the CustomValue
 *   stories).
 * - Items: prefer `defaultItems`. Despite the name it re-renders when the
 *   prop changes ("uncontrolled" refers only to filtering); it enables the
 *   built-in filter and the full-list-on-click behavior, both of which are
 *   lost with controlled `items`, which means "the caller filters" — reserve
 *   it for async/server-side filtering.
 * - The popup open state is not controllable (react-aria limitation).
 */
export const Combobox = <
  T extends object,
  M extends ComboboxSelectionMode = "single",
>(
  props: ComboboxProps<T, M>,
) => {
  const { className, ...ariaProps } = props;
  const { contains } = useFilter({ sensitivity: "base" });

  // Ignore whitespace on both sides so "johnsmith" still finds "John Smith",
  // as the select this family replaced did. Case and accents are handled by the
  // locale-aware collator behind `contains`. Kept stable: react-stately lists
  // the filter in the deps of its filtered-collection memo, so a new function
  // every render re-filters the whole collection every render.
  const defaultFilter = useCallback(
    (textValue: string, inputValue: string) =>
      contains(stripWhitespace(textValue), stripWhitespace(inputValue)),
    [contains],
  );

  return (
    <AriaComboBox
      defaultFilter={defaultFilter}
      {...ariaProps}
      // After the spread, not before: turning it off would snap the popover
      // shut instead of showing ComboboxContent's empty state, which is the
      // family's own behavior rather than the caller's to pick.
      allowsEmptyCollection
      className={comboboxStyle().root({ className })}
    />
  );
};

const stripWhitespace = (value: string) => value.replace(/\s+/g, "");
